import { useCrossSell } from '@/hooks/patient/refill/useCrossSell';
import { Logger } from '@/utils/logger';
import { Box, Button } from '@montugroup/design-system';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { Wrapper } from '../order-history-card/OrderHistoryCard.styles';
import { LineItem } from './LineItem';
import OrderProductsSummary from './OrderProductsSummary';
import { OrderProductTaxDisclaimer } from './OrderProductTaxDisclaimer';
import { Title } from './Styles';
import type { CheckoutData } from './types';

const logger = new Logger('RefillOrderSummary');

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'stretch',
  marginTop: theme.spacing(5),
}));

export type OrderSummaryProps = {
  checkoutData: CheckoutData;
  onCheckout: () => Promise<void>;
  disabled?: boolean;
};

export function RefillOrderSummary(props: OrderSummaryProps) {
  const {
    checkoutData: { products, totalPrice },
    onCheckout,
    disabled,
  } = { ...props };
  const [isLoading, setIsLoading] = useState(false);
  const { showCrossSellPage } = useCrossSell();
  const label = showCrossSellPage ? 'Proceed to shipping' : 'Proceed';
  const formattedTotalAmount = `$${totalPrice.toFixed(2)}`;
  const handleCheckout = async () => {
    setIsLoading(true);
    try {
      await onCheckout();
    } catch (error) {
      logger.error('handleCheckout', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ marginTop: 2, margin: { sm: 0 } }}>
      <Wrapper role="region" aria-labelledby="order-summary-title">
        <Box sx={{ marginBottom: 3 }}>
          <Title id="order-summary-title">Summary</Title>
        </Box>
        <OrderProductsSummary products={products} />
        <LineItem isTotal label="Total" amount={formattedTotalAmount} />
        <OrderProductTaxDisclaimer sx={{ mt: 1 }} />
      </Wrapper>

      <ButtonContainer>
        <Button
          onClick={handleCheckout}
          color="primary"
          variant="contained"
          size="large"
          disabled={disabled}
          data-testid="refill-button"
          loading={isLoading}
        >
          {label}
        </Button>
      </ButtonContainer>
    </Box>
  );
}

export default RefillOrderSummary;
